import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { getReferrals } from "../../helpers/api/referral";
import type { ReferralListItem } from "../../types/api.generated";

interface InviteFriendsModalProps {
  username: string;
  onClose: () => void;
}

const formatRewardedDate = (value: string | null) => {
  if (!value) return "Referral completed";

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
};

const getInitial = (username: string | null) => username?.trim().charAt(0).toUpperCase() || "?";

const InviteFriendsModal = ({ username, onClose }: InviteFriendsModalProps) => {
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [referrals, setReferrals] = useState<ReferralListItem[]>([]);
  const [totalRewarded, setTotalRewarded] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");
  const referralLink = `${window.location.origin}/ref/${encodeURIComponent(username)}`;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>("button, input");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    let active = true;

    const loadReferrals = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await getReferrals({ page: 1, limit: 5, status: "rewarded" });
        if (!active) return;
        setReferrals(response.items);
        setTotalRewarded(response.summary.rewardedReferrals);
      } catch {
        if (active) setError("We couldn't load your referrals. Please try again later.");
      } finally {
        if (active) setIsLoading(false);
      }
    };

    void loadReferrals();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (copyStatus === "idle") return undefined;
    const timeout = window.setTimeout(() => setCopyStatus("idle"), 2000);
    return () => window.clearTimeout(timeout);
  }, [copyStatus]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
  };

  return createPortal(
    <div
      className="invite-friends-modal__backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      data-cy="invite-friends-backdrop"
      data-testid="invite-backdrop"
    >
      <section
        ref={dialogRef}
        className="invite-friends-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-friends-title"
        data-cy="invite-friends-modal"
      >
        <header className="invite-friends-modal__header">
          <div className="invite-friends-modal__title-icon" aria-hidden="true">
            <i className="mdi mdi-gift-outline" />
          </div>
          <div>
            <p className="invite-friends-modal__eyebrow">Referral program</p>
            <h2 id="invite-friends-title">Invite friends</h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="invite-friends-modal__close"
            aria-label="Close invite friends"
            onClick={onClose}
            data-cy="invite-friends-close"
          >
            <i className="mdi mdi-close" aria-hidden="true" />
          </button>
        </header>

        <div className="invite-friends-modal__summary" aria-label={`${totalRewarded} successful referrals`}>
          <span>Successful referrals</span>
          <strong data-cy="invite-friends-total">{totalRewarded}</strong>
        </div>

        <section className="invite-friends-modal__section" aria-labelledby="share-referral-link">
          <h3 id="share-referral-link">Share your link</h3>
          <div className="invite-friends-modal__share-row">
            <input type="text" value={referralLink} readOnly aria-label="Your referral link" />
            <button type="button" onClick={copyLink} data-cy="copy-referral-link">
              <i className="mdi mdi-content-copy" aria-hidden="true" />
              {copyStatus === "copied" ? "Link copied" : "Copy link"}
            </button>
          </div>
          <p className={`invite-friends-modal__copy-status${copyStatus === "error" ? " is-error" : ""}`} aria-live="polite">
            {copyStatus === "error" ? "Unable to copy the link." : copyStatus === "copied" ? "Link copied" : ""}
          </p>
        </section>

        <section className="invite-friends-modal__section" aria-labelledby="how-referrals-work">
          <h3 id="how-referrals-work">How it works</h3>
          <ol className="invite-friends-modal__steps">
            <li>
              <i className="mdi mdi-link-variant" aria-hidden="true" />
              <span><strong>Share your arena link</strong>Send your personal invite to a future champion.</span>
            </li>
            <li>
              <i className="mdi mdi-account-plus-outline" aria-hidden="true" />
              <span><strong>They join the arena</strong>Your friend signs up through your referral link.</span>
            </li>
            <li>
              <i className="mdi mdi-trophy-outline" aria-hidden="true" />
              <span><strong>Their first bet seals it</strong>The referral is validated after their first wager.</span>
            </li>
          </ol>
        </section>

        <section className="invite-friends-modal__section" aria-labelledby="recent-referrals">
          <h3 id="recent-referrals">Latest referrals</h3>
          {isLoading && <p className="invite-friends-modal__state" role="status">Loading referrals...</p>}
          {!isLoading && error && <p className="invite-friends-modal__state is-error" role="alert">{error}</p>}
          {!isLoading && !error && referrals.length === 0 && (
            <p className="invite-friends-modal__state">No successful referrals yet. Your next invite could be the first.</p>
          )}
          {!isLoading && !error && referrals.length > 0 && (
            <ul className="invite-friends-modal__list" data-cy="recent-referrals">
              {referrals.map((referral) => (
                <li key={referral.id}>
                  <span className="invite-friends-modal__avatar" aria-hidden="true">
                    {getInitial(referral.referee.username)}
                  </span>
                  <span>
                    <strong>{referral.referee.username || "Arena player"}</strong>
                    <small>{formatRewardedDate(referral.rewardedAt)}</small>
                  </span>
                  <span className="invite-friends-modal__rewarded">Rewarded</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </section>
    </div>,
    document.body
  );
};

export default InviteFriendsModal;
